import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { 
    ArrowLeft, ArrowRight, BookOpen, Clock, Calendar,
    CheckCircle, AlertTriangle, XCircle, Target,
    TrendingUp, BarChart3, Zap, Search, Smartphone,
    MousePointerClick, Share2, Mail, Link2, Check
} from 'lucide-react';
import SEO from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

export default function BlogPostWebsiteGrade({ onRunAudit, onNavigate }) {
    const [linkCopied, setLinkCopied] = useState(false);
    
    const seo = {
        title: "What a Website Grade Really Means | SiteGradePro Blog",
        description: "Understanding your website score and how to interpret the metrics that matter for your business. Learn what A, B, C, D, and F grades mean for SEO, performance, and conversions.",
        keywords: "website grade meaning, website score explained, SEO grade, website audit score, site performance grade"
    };

    const shareUrl = typeof window !== 'undefined' ? window.location.href : "https://sitegradepro.com/blog/what-website-grade-means";
    const shareTitle = "What a Website Grade Really Means";
    const shareDescription = "Understanding your website score and how to interpret the metrics that matter for your business.";
    
    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        });
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO title={seo.title} description={seo.description} keywords={seo.keywords} />
            
            <Navbar activePage="blog" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            {/* Article Header */}
            <header className="py-12 px-4 border-b border-white/5">
                <div className="max-w-3xl mx-auto">
                    <button 
                        onClick={() => onNavigate('blog')}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
                        data-testid="back-to-blog"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Blog
                    </button>
                    
                    <div className="flex items-center gap-3 mb-6">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            Education
                        </span>
                        <span className="text-gray-500 text-sm flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            March 2026
                        </span>
                        <span className="text-gray-500 text-sm flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            5 min read
                        </span>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                        What a Website Grade Really Means
                    </h1>
                    
                    <p className="text-xl text-gray-400 leading-relaxed">
                        Understanding your website score and how to interpret the metrics that matter for your business success.
                    </p>
                </div>
            </header>

            {/* Article Content */}
            <article className="py-12 px-4" data-testid="blog-article-content">
                <div className="max-w-3xl mx-auto prose prose-invert prose-lg">
                    
                    {/* Introduction */}
                    <p className="text-gray-300 leading-relaxed">
                        You've just run a website audit and received your grade. Maybe it's a <span className="text-emerald-400 font-semibold">B+</span>, 
                        or perhaps a concerning <span className="text-red-400 font-semibold">D-</span>. But what do these letters actually mean 
                        for your business? More importantly, how should they influence your website improvement strategy?
                    </p>
                    
                    <p className="text-gray-300 leading-relaxed">
                        In this guide, we'll break down exactly what website grades represent, how they're calculated, 
                        and what actions you should take based on your score.
                    </p>

                    {/* The Grading Scale */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
                        The Website Grading Scale Explained
                    </h2>
                    
                    <p className="text-gray-300 leading-relaxed mb-8">
                        Website grades follow a familiar A-F scale, but each letter represents specific performance 
                        thresholds across multiple categories:
                    </p>

                    <div className="space-y-4 my-8">
                        <Card className="bg-emerald-500/10 border-emerald-500/30">
                            <CardContent className="p-6 flex items-start gap-4">
                                <div className="text-4xl font-bold text-emerald-400">A</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Excellent (90-100%)</h3>
                                    <p className="text-gray-400 text-sm">
                                        Your website excels in all major areas. You're following best practices and likely 
                                        outperforming competitors. Focus on maintaining this level and exploring advanced optimizations.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-cyan-500/10 border-cyan-500/30">
                            <CardContent className="p-6 flex items-start gap-4">
                                <div className="text-4xl font-bold text-cyan-400">B</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Good (80-89%)</h3>
                                    <p className="text-gray-400 text-sm">
                                        Your site is performing well with room for improvement. A few targeted fixes could 
                                        push you into the A range and give you a competitive edge.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-yellow-500/10 border-yellow-500/30">
                            <CardContent className="p-6 flex items-start gap-4">
                                <div className="text-4xl font-bold text-yellow-400">C</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Average (70-79%)</h3>
                                    <p className="text-gray-400 text-sm">
                                        Your website meets basic standards but has noticeable gaps. You're likely losing 
                                        potential customers to competitors with better-optimized sites.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-orange-500/10 border-orange-500/30">
                            <CardContent className="p-6 flex items-start gap-4">
                                <div className="text-4xl font-bold text-orange-400">D</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Below Average (60-69%)</h3>
                                    <p className="text-gray-400 text-sm">
                                        Significant issues are affecting your website's performance. Users may be leaving 
                                        due to slow speeds, poor mobile experience, or confusing navigation.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-red-500/10 border-red-500/30">
                            <CardContent className="p-6 flex items-start gap-4">
                                <div className="text-4xl font-bold text-red-400">F</div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-1">Poor (Below 60%)</h3>
                                    <p className="text-gray-400 text-sm">
                                        Critical issues require immediate attention. Your website may be actively hurting 
                                        your business through lost traffic, poor search rankings, and frustrated visitors.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* What Makes Up Your Grade */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
                        The Four Pillars of Your Website Grade
                    </h2>
                    
                    <p className="text-gray-300 leading-relaxed mb-8">
                        Your overall grade is calculated from four key categories, each measuring different aspects 
                        of your website's health:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center mb-4">
                                    <Search className="h-6 w-6 text-cyan-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">1. SEO Structure</h3>
                                <p className="text-gray-400 text-sm mb-4">
                                    How well search engines can understand and rank your content.
                                </p>
                                <ul className="text-sm text-gray-500 space-y-1">
                                    <li>• Title tags & meta descriptions</li>
                                    <li>• Header hierarchy (H1, H2, H3)</li>
                                    <li>• Keyword optimization</li>
                                    <li>• Internal linking structure</li>
                                </ul>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center mb-4">
                                    <Zap className="h-6 w-6 text-yellow-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">2. Performance</h3>
                                <p className="text-gray-400 text-sm mb-4">
                                    How fast your website loads and responds to user interactions.
                                </p>
                                <ul className="text-sm text-gray-500 space-y-1">
                                    <li>• Page load speed</li>
                                    <li>• Image optimization</li>
                                    <li>• Script efficiency</li>
                                    <li>• Server response time</li>
                                </ul>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center mb-4">
                                    <Smartphone className="h-6 w-6 text-green-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">3. Mobile Optimization</h3>
                                <p className="text-gray-400 text-sm mb-4">
                                    How well your site works on phones and tablets.
                                </p>
                                <ul className="text-sm text-gray-500 space-y-1">
                                    <li>• Responsive design</li>
                                    <li>• Touch-friendly elements</li>
                                    <li>• Mobile page speed</li>
                                    <li>• Viewport configuration</li>
                                </ul>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-6">
                                <div className="w-12 h-12 rounded-lg bg-magenta/20 flex items-center justify-center mb-4">
                                    <MousePointerClick className="h-6 w-6 text-magenta" />
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-2">4. Conversion & UX</h3>
                                <p className="text-gray-400 text-sm mb-4">
                                    How effectively your site converts visitors into customers.
                                </p>
                                <ul className="text-sm text-gray-500 space-y-1">
                                    <li>• Call-to-action clarity</li>
                                    <li>• Trust signals</li>
                                    <li>• Visual hierarchy</li>
                                    <li>• User flow optimization</li>
                                </ul>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Why Your Grade Matters */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
                        Why Your Website Grade Actually Matters
                    </h2>
                    
                    <p className="text-gray-300 leading-relaxed">
                        Your website grade isn't just a vanity metric—it directly correlates with real business outcomes:
                    </p>

                    <div className="bg-white/5 border border-white/10 rounded-lg p-6 my-8">
                        <h3 className="text-lg font-semibold text-white mb-4">The Business Impact of Website Grades</h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <TrendingUp className="h-5 w-5 text-emerald-400 mt-1" />
                                <div>
                                    <span className="text-white font-medium">Search Rankings:</span>
                                    <span className="text-gray-400"> Google uses page speed and mobile-friendliness as ranking factors. 
                                    A low grade means lower visibility in search results.</span>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Target className="h-5 w-5 text-cyan-400 mt-1" />
                                <div>
                                    <span className="text-white font-medium">Conversion Rates:</span>
                                    <span className="text-gray-400"> Studies show that a 1-second delay in page load can reduce 
                                    conversions by 7%. Poor UX compounds this effect.</span>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <BarChart3 className="h-5 w-5 text-yellow-400 mt-1" />
                                <div>
                                    <span className="text-white font-medium">Bounce Rate:</span>
                                    <span className="text-gray-400"> 53% of mobile users abandon sites that take longer than 3 seconds 
                                    to load. Your grade reflects this risk.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* What To Do With Your Grade */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
                        What To Do Based on Your Grade
                    </h2>

                    <div className="space-y-6 my-8">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="h-5 w-5 text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-2">If You Got an A or B</h3>
                                <p className="text-gray-400">
                                    Congratulations! Focus on maintaining your score by monitoring regularly (monthly audits are ideal). 
                                    Look for advanced optimizations like Core Web Vitals improvements, A/B testing your CTAs, 
                                    and expanding your content strategy.
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-2">If You Got a C</h3>
                                <p className="text-gray-400">
                                    You have a solid foundation but need targeted improvements. Prioritize the category with the lowest 
                                    score first. Often, quick wins like optimizing images or adding missing meta descriptions can 
                                    bump you into B territory.
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                                <XCircle className="h-5 w-5 text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-2">If You Got a D or F</h3>
                                <p className="text-gray-400">
                                    Don't panic—this is actually an opportunity. Major improvements are within reach. Start with the 
                                    "critical" issues flagged in your report. Consider whether a website redesign might be more 
                                    cost-effective than patching multiple problems.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Key Takeaways */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mt-12 mb-6">
                        Key Takeaways
                    </h2>
                    
                    <Card className="bg-gradient-to-br from-magenta/10 to-violet-500/10 border-magenta/30 my-8">
                        <CardContent className="p-6">
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3 text-gray-300">
                                    <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    Your website grade reflects real-world business impact on traffic, rankings, and conversions
                                </li>
                                <li className="flex items-start gap-3 text-gray-300">
                                    <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    Focus on the lowest-scoring category first for maximum improvement
                                </li>
                                <li className="flex items-start gap-3 text-gray-300">
                                    <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    Regular audits (monthly) help you catch issues before they impact your business
                                </li>
                                <li className="flex items-start gap-3 text-gray-300">
                                    <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                                    Even small improvements can have significant compound effects over time
                                </li>
                            </ul>
                        </CardContent>
                    </Card>

                    <p className="text-gray-300 leading-relaxed">
                        Remember: a website grade is a starting point, not a final verdict. The most successful businesses 
                        treat website optimization as an ongoing process, continuously improving based on data and user feedback.
                    </p>

                </div>
            </article>

            {/* CTA Section */}
            <section className="py-16 px-4 border-t border-white/5">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        Ready to See Your Website Grade?
                    </h2>
                    <p className="text-gray-400 mb-8">
                        Get your free website audit and discover exactly where your site stands.
                    </p>
                    <Button 
                        onClick={onRunAudit}
                        size="lg"
                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                        data-testid="blog-cta-audit"
                    >
                        Run Free Audit Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                </div>
            </section>

            {/* Share Section */}
            <section className="py-8 px-4 border-t border-white/5" data-testid="share-section">
                <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-gray-400 flex items-center gap-2">
                        <Share2 className="h-4 w-4" />
                        Share this article
                    </span>
                    <div className="flex items-center gap-3">
                        <a 
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/5 hover:bg-[#1DA1F2]/20 rounded-lg transition-colors group"
                            data-testid="share-twitter"
                            title="Share on X (Twitter)"
                        >
                            <svg className="h-5 w-5 text-gray-400 group-hover:text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </a>
                        <a 
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/5 hover:bg-[#1877F2]/20 rounded-lg transition-colors group"
                            data-testid="share-facebook"
                            title="Share on Facebook"
                        >
                            <svg className="h-5 w-5 text-gray-400 group-hover:text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </a>
                        <a 
                            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/5 hover:bg-[#0A66C2]/20 rounded-lg transition-colors group"
                            data-testid="share-linkedin"
                            title="Share on LinkedIn"
                        >
                            <svg className="h-5 w-5 text-gray-400 group-hover:text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </a>
                        <a 
                            href={`https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white/5 hover:bg-[#FF4500]/20 rounded-lg transition-colors group"
                            data-testid="share-reddit"
                            title="Share on Reddit"
                        >
                            <svg className="h-5 w-5 text-gray-400 group-hover:text-[#FF4500]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
                        </a>
                        <a 
                            href={`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${shareDescription}\n\nRead more: ${shareUrl}`)}`}
                            className="p-2 bg-white/5 hover:bg-emerald-500/20 rounded-lg transition-colors group"
                            data-testid="share-email"
                            title="Share via Email"
                        >
                            <Mail className="h-5 w-5 text-gray-400 group-hover:text-emerald-400" />
                        </a>
                        <button 
                            onClick={handleCopyLink}
                            className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group"
                            data-testid="share-copy-link"
                            title={linkCopied ? "Link copied!" : "Copy link"}
                        >
                            {linkCopied ? (
                                <Check className="h-5 w-5 text-emerald-400" />
                            ) : (
                                <Link2 className="h-5 w-5 text-gray-400 group-hover:text-white" />
                            )}
                        </button>
                    </div>
                </div>
            </section>

            {/* Related Articles */}
            <section className="py-12 px-4 bg-white/5">
                <div className="max-w-3xl mx-auto">
                    <h3 className="text-xl font-bold text-white mb-6">Related Articles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button 
                            onClick={() => onNavigate('blog')}
                            className="text-left p-4 bg-black/50 border border-white/10 rounded-lg hover:border-magenta/30 transition-colors"
                        >
                            <span className="text-xs text-cyan-400 font-medium">CONVERSION</span>
                            <p className="text-white font-medium mt-1">Why Your Website Isn't Converting</p>
                            <span className="text-gray-500 text-sm">Coming Soon</span>
                        </button>
                        <button 
                            onClick={() => onNavigate('blog')}
                            className="text-left p-4 bg-black/50 border border-white/10 rounded-lg hover:border-magenta/30 transition-colors"
                        >
                            <span className="text-xs text-magenta font-medium">SEO</span>
                            <p className="text-white font-medium mt-1">5 SEO Mistakes Small Businesses Make</p>
                            <span className="text-gray-500 text-sm">Coming Soon</span>
                        </button>
                    </div>
                </div>
            </section>

            <Footer onNavigate={onNavigate} />
        </div>
    );
}
